// ============================================================================
//  PatientHeaderCard — the patient banner at the top of a consulting room
// ----------------------------------------------------------------------------
//  ONE COPY, WHERE THERE WERE SEVEN
//
//  This block was hand-pasted into seven files with no shared component, no
//  shared style constants, and inline colour literals repeated verbatim in every
//  copy. It had drifted into three generations that no longer agreed:
//
//    · the status chip is amber in five copies and brick red in Doctors/OutPatient
//    · it reads `status` in some and `state` in others
//    · SpO2, FBS and RBS exist in two copies and are simply absent from the rest
//    · the age line is formatted three different ways
//    · the degree sign in two copies is a literal U+FFFD replacement character —
//      the file was decoded as CP1252 once and the real ° did not survive
//
//  Every one of those is the same failure: a change made in one copy and not the
//  others. So the four consulting rooms now render THIS, and the differences that
//  are real become props.
//
// ----------------------------------------------------------------------------
//  THE ARRANGEMENT, AND WHY IT CHANGED
//
//  The old layout stacked the status chip, the visit date and the Special
//  Precaution button UNDERNEATH the contact details, in the middle column. Three
//  stacked items made the middle column the tallest thing on the card, and since
//  a row is as tall as its tallest cell, that height was charged to the whole
//  banner — pushing the clinical notes below the fold on a laptop.
//
//  They are now their own column on the right, in the space the vitals block was
//  leaving empty. Same information, one line of height instead of four.
//
//  FLEX, NOT GRID. The original used a 12-column MUI Grid with four children of
//  md={3}, except the last one was missing its `size` prop — so on the newer Grid
//  it fell back to the legacy breakpoint and the row wrapped unevenly. Five
//  columns also do not divide into twelve. Flex with per-column basis expresses
//  "share the row, wrap when narrow" directly and has no such arithmetic.
// ============================================================================

import React from 'react';
//  No Grid: this lays out with flex. See the note above on why.
import {
  Box, Avatar, Chip, Stack, Typography, Button, IconButton, Tooltip,
} from '@mui/material';
import { History, Lock as LockIcon, MedicalInformation } from '@mui/icons-material';
import moment from 'moment';

/*  The literals that were repeated in all seven copies, named once. #1A9B7F
 *  alone appeared 323 times across 41 files with no constant anywhere. */
export const HEADER = {
  teal:      '#1A9B7F',
  tealLine:  'rgba(26,155,127,0.30)',
  redLine:   'rgba(220,38,38,0.30)',
  surface:   '#f8fafc',
  ink:       '#0f172a',
  inkSoft:   '#64748b',
  male:      'rgba(33, 150, 243, 0.9)',
  female:    'rgba(244, 67, 54, 0.9)',
  insurance: 'rgba(255, 152, 0, 0.9)',
  status:    'rgba(255, 193, 7, 0.95)',
  abnormal:  '#DC2626',
};

//  The degree sign as an ESCAPE, not a literal byte. Two of the seven copies had
//  already lost it to a bad encoding round-trip and were rendering "36.7?C" in
//  front of clinicians. ° cannot be corrupted by re-saving the file.
const DEG = '°';   // °
const SUB2 = '₂';  // ₂ , for SpO₂

const initialsOf = (name) => String(name || '')
  .split(' ').filter(Boolean).map((n) => n[0]).join('').slice(0, 2).toUpperCase();

//  One vital, with its unit. `value` of null/''/undefined shows a dash rather
//  than an empty gap, so a missing reading is visibly missing.
const Vital = ({ label, value, unit, abnormal }) => (
  <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.5 }}>
    <strong>{label}:</strong>{' '}
    <Box component="span" sx={{
      color: abnormal ? HEADER.abnormal : 'inherit',
      fontWeight: abnormal ? 700 : 400,
    }}>
      {value === null || value === undefined || value === '' ? '—' : value}
      {value === null || value === undefined || value === '' ? '' : (unit || '')}
    </Box>
  </Typography>
);

//  A labelled contact/registration line. Renders NOTHING when empty, so a blank
//  value never leaves a dangling label — which is what the guards in the original
//  copies were doing by hand, inconsistently.
const Meta = ({ label, value, icon }) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  return (
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, lineHeight: 1.6, color: HEADER.ink }}>
      {icon ? `${icon} ` : ''}{label ? <strong>{label}: </strong> : null}{value}
    </Typography>
  );
};

const colHead = {
  fontSize: '0.66rem', fontWeight: 800, letterSpacing: '.07em',
  textTransform: 'uppercase', color: HEADER.inkSoft, mb: 0.5,
};

export default function PatientHeaderCard({
  patient,
  //  The status chip's text. Two of the four screens keep it on `status` and two
  //  on `state`, and unifying the DATA is a bigger change than this component —
  //  so the caller says which, and the chip is one colour everywhere.
  statusLabel,
  ageText,
  //  Emergency's banner is bordered red rather than teal. The one visual
  //  difference between the four that is deliberate.
  tone = 'teal',
  onPrecaution,
  onVitalsHistory,
  //  Given a handler, the green Ward Rounds button appears. Three of the seven
  //  copies had it and four did not, for no reason anyone recorded.
  onWardRounds,
  wardRoundsLabel = 'Ward Rounds / Information',
  //  { startedAt, timeLeft } to show the 30-hour detention countdown. Was ~70
  //  identical lines pasted into each ward file.
  detention = null,
  //  Anything a particular screen needs and the others do not.
  extra = null,
}) {
  if (!patient) return null;
  const v = patient.vitals || {};

  //  The thresholds the two copies that HAD these fields were using. Kept, and
  //  now applied on every screen rather than two of them.
  const fbsHigh = v.fbs != null && v.fbs !== '' && parseFloat(v.fbs) > 7;
  const rbsHigh = v.rbs != null && v.rbs !== '' && parseFloat(v.rbs) > 11.1;

  return (
    <Box sx={{
      px: 1.5, py: 1.25,
      borderBottom: '1px solid',
      borderColor: tone === 'red' ? HEADER.redLine : HEADER.tealLine,
      bgcolor: HEADER.surface,
    }}>
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start',
        gap: 2, rowGap: 1.5,
      }}>
        {/* ── 1. Who ─────────────────────────────────────────────────────── */}
        <Box sx={{ flex: '1 1 250px', minWidth: 220 }}>
          <Box display="flex" alignItems="center" gap={1.25}>
            <Avatar sx={{
              bgcolor: '#fff', color: HEADER.teal, width: 42, height: 42,
              fontSize: 15, fontWeight: 800,
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            }}>
              {initialsOf(patient.fullName)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.95rem', fontWeight: 700, color: HEADER.ink, lineHeight: 1.2,
              }}>
                {patient.fullName}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: HEADER.inkSoft, mt: 0.2, mb: 0.5 }}>
                {ageText || `${patient.age} yrs`} · ID {patient.id}
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={patient.gender} sx={{
                  height: 20, fontSize: '0.66rem', fontWeight: 700, color: '#fff',
                  bgcolor: String(patient.gender).toUpperCase() === 'MALE'
                    ? HEADER.male : HEADER.female,
                }} />
                {patient.insurance && (
                  <Chip size="small" label={patient.insurance} sx={{
                    height: 20, fontSize: '0.66rem', fontWeight: 700, color: '#fff',
                    bgcolor: HEADER.insurance, maxWidth: 210,
                  }} />
                )}
              </Stack>
            </Box>
          </Box>

          {onWardRounds && (
            <Button
              size="small"
              onClick={onWardRounds}
              startIcon={<MedicalInformation sx={{ fontSize: 16 }} />}
              sx={{
                mt: 1, textTransform: 'none', fontWeight: 700, fontSize: '0.74rem',
                color: '#fff', px: 1.5,
                background: `linear-gradient(135deg, ${HEADER.teal}, #12c48f)`,
                '&:hover': { background: `linear-gradient(135deg, #158a71, ${HEADER.teal})` },
              }}
            >
              {wardRoundsLabel}
            </Button>
          )}
        </Box>

        {/* ── 2. Contact and registration ────────────────────────────────── */}
        <Box sx={{ flex: '1 1 175px', minWidth: 160 }}>
          <Typography sx={colHead}>Contact</Typography>
          <Meta icon="📞" value={patient.tel} />
          <Meta icon="📍" value={patient.location} />
          <Meta label="Occupation" value={patient.occ} />
          <Meta label="Marital status" value={patient.ma_stu} />
        </Box>

        {/* ── 3. Vital signs ─────────────────────────────────────────────── */}
        <Box sx={{ flex: '1 1 185px', minWidth: 170 }}>
          <Typography sx={colHead}>Vital signs</Typography>
          <Vital label="Temperature"      value={v.temperature} unit={`${DEG}C`} />
          <Vital label="Blood pressure"   value={v.bp}          unit=" mmHg" />
          <Vital label="Pulse"            value={v.pulse}       unit=" bpm" />
          <Vital label="Respiration"      value={v.respiration} unit=" /min" />
          <Vital label={`SpO${SUB2}`}     value={v.spo2}        unit="%" />
        </Box>

        {/* ── 4. Measurements ────────────────────────────────────────────── */}
        <Box sx={{ flex: '1 1 150px', minWidth: 140 }}>
          <Typography sx={colHead}>Measurements</Typography>
          <Vital label="Weight" value={v.weight} unit=" kg" />
          <Vital label="Height" value={v.height} unit=" cm" />
          <Vital label="BMI"    value={v.bmi} />
          {/*  FBS and RBS were rendered on only two of the seven screens, and on
               one of those the loader never selected them — so they printed a
               dash whatever the nurse recorded. Here on all four now. */}
          <Vital label="FBS" value={v.fbs} unit=" mmol/L" abnormal={fbsHigh} />
          <Vital label="RBS" value={v.rbs} unit=" mmol/L" abnormal={rbsHigh} />
        </Box>

        {/* ── 5. Visit, status and actions ───────────────────────────────────
             MOVED HERE from beneath the contact column, which is the whole point
             of this rearrangement — see the note at the top of the file. */}
        <Box sx={{
          flex: '0 1 168px', minWidth: 150,
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75,
        }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', gap: 1,
          }}>
            <Typography sx={{ ...colHead, mb: 0 }}>Visit</Typography>
            {onVitalsHistory && (
              <Tooltip title="Vital signs history">
                <IconButton size="small" onClick={onVitalsHistory} sx={{
                  width: 28, height: 28,
                  bgcolor: HEADER.teal, color: '#fff',
                  '&:hover': { bgcolor: '#12816a' },
                }}>
                  <History sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {statusLabel && (
            <Chip size="small" label={statusLabel} sx={{
              height: 22, fontSize: '0.68rem', fontWeight: 800,
              bgcolor: HEADER.status, color: '#fff',
            }} />
          )}
          <Meta label="Date" value={patient.visitDate} />

          {onPrecaution && (
            <Button
              variant="outlined" color="warning" size="small"
              onClick={onPrecaution}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: '0.72rem',
                borderWidth: 2, py: 0.25,
                '&:hover': { borderWidth: 2, boxShadow: '0 4px 12px rgba(255,152,0,0.3)' },
              }}
            >
              ⚠️ Special Precaution
            </Button>
          )}
        </Box>
      </Box>

      {/*  The detention countdown — full width beneath, because it is an alert
          about the whole visit rather than a field of it, and because at 30 hours
          it is the most time-critical thing on the screen. */}
      {detention && <DetentionCountdown {...detention} />}
      {extra}
    </Box>
  );
}

/*  Thirty hours from admission, as a bar. `startedAt` is the admission moment and
 *  `timeLeft` the already-computed label the ward screens tick every minute —
 *  passed in rather than recomputed here so the bar and the label cannot disagree.
 */
function DetentionCountdown({ startedAt, timeLeft }) {
  const start = moment(startedAt);
  const elapsed = start.isValid() ? moment().diff(start, 'minutes') : 0;
  const pct = Math.max(0, Math.min(100, (elapsed / (30 * 60)) * 100));

  return (
    <Box sx={{
      mt: 1.25, px: 1.5, py: 1, borderRadius: 2,
      bgcolor: 'warning.main', color: '#fff',
      boxShadow: '0 4px 12px rgba(255,152,0,0.4)',
    }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.75}>
        <Box display="flex" alignItems="center" gap={0.75}>
          <LockIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: 1 }}>
            DETENTION
          </Typography>
        </Box>
        {timeLeft && (
          <Typography sx={{
            fontSize: '0.76rem', fontWeight: 700, fontFamily: 'monospace',
            bgcolor: 'rgba(0,0,0,0.2)', px: 1, py: 0.25, borderRadius: 1,
          }}>
            {timeLeft === 'EXPIRED' ? '⚠️ EXPIRED' : `⏱️ ${timeLeft}`}
          </Typography>
        )}
      </Box>
      <Box sx={{ width: '100%', bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1, height: 6 }}>
        <Box sx={{
          width: `${pct}%`, height: '100%', borderRadius: 1,
          transition: 'width .5s ease',
          bgcolor: pct > 90 ? 'error.main' : pct > 70 ? 'warning.light' : '#4caf50',
        }} />
      </Box>
    </Box>
  );
}
